import React, {
  useMemo,
  useState,
} from "react";

import {
  prepareChildrensBookManuscriptImport,
} from "../utils/childrensBookManuscriptImportApplication";


function clean(value) {
  return String(
    value || ""
  ).trim();
}


function countLabel(
  count,
  singular,
  plural
) {
  return `${
    count
  } ${
    count === 1
      ? singular
      : plural
  }`;
}


function unitTypeLabel(
  value,
  isFR
) {
  const labels = {
    single_page:
      isFR
        ? "Page simple"
        : "Single page",

    double_page_spread:
      isFR
        ? "Double page"
        : "Two-page spread",

    front_matter:
      isFR
        ? "Page liminaire"
        : "Front matter page",

    back_matter:
      isFR
        ? "Page finale"
        : "Back matter page",

    activity_page:
      isFR
        ? "Page d’activité"
        : "Activity page",
  };

  return (
    labels[
      clean(value)
    ] ||
    (
      isFR
        ? "Page ou double page"
        : "Page or spread"
    )
  );
}


function unitPageLabel(
  unit,
  isFR
) {
  if (!unit) return "";

  const title =
    clean(
      unit?.workingTitle
    ) ||
    unitTypeLabel(
      unit?.unitType,
      isFR
    );

  const position =
    clean(
      unit?.pageNumber
    );

  if (!position) {
    return title;
  }

  const pageNumber =
    Number(position);

  if (
    !Number.isInteger(
      pageNumber
    ) ||
    pageNumber < 1
  ) {
    return `${title} — ${position}`;
  }

  if (
    clean(
      unit?.unitType
    ) ===
    "double_page_spread"
  ) {
    return `${title} — Pages ${
      pageNumber
    } ${
      isFR
        ? "à"
        : "to"
    } ${
      pageNumber + 1
    }`;
  }

  return `${title} — Page ${pageNumber}`;
}


function replacementUnitLabels(
  plan,
  units,
  isFR
) {
  const safeUnits =
    Array.isArray(units)
      ? units
      : [];

  const replacementIds =
    [
      ...new Set(
        (
          Array.isArray(
            plan?.operations
          )
            ? plan.operations
            : []
        )
          .filter(
            (operation) =>
              clean(
                operation
                  ?.decision
              ) ===
              "replace"
          )
          .map(
            (operation) =>
              clean(
                operation
                  ?.unitId
              )
          )
          .filter(Boolean)
      ),
    ];

  return replacementIds
    .map(
      (unitId) => {
        const unit =
          safeUnits.find(
            (candidate) =>
              clean(
                candidate?.id
              ) ===
              unitId
          );

        return unitPageLabel(
          unit,
          isFR
        );
      }
    )
    .filter(Boolean);
}


function naturalList(
  values,
  isFR
) {
  const items =
    Array.isArray(values)
      ? values.filter(Boolean)
      : [];

  if (items.length === 0) {
    return "";
  }

  if (items.length === 1) {
    return items[0];
  }

  const last =
    items[
      items.length - 1
    ];

  const first =
    items.slice(
      0,
      -1
    );

  return `${
    first.join(", ")
  } ${
    isFR
      ? "et"
      : "and"
  } ${last}`;
}


export default function ChildrensBookManuscriptImportApplyPanel({
  lang = "en",
  sections = [],
  bookMapUnits = [],
  manuscriptPlan = {
    entries: [],
  },
  mappingBySection = {},
  decisionBySection = {},
  onApplyManuscriptImport,
  onApplied,
}) {
  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const [
    applying,
    setApplying,
  ] = useState(false);

  const [
    pendingReplacement,
    setPendingReplacement,
  ] = useState(null);

  const [
    notice,
    setNotice,
  ] = useState({
    type: "",
    message: "",
  });

  const prepared =
    useMemo(
      () =>
        prepareChildrensBookManuscriptImport({
          sections,
          bookMapUnits,
          manuscriptPlan,
          mappingBySection,
          decisionBySection,
        }),
      [
        sections,
        bookMapUnits,
        manuscriptPlan,
        mappingBySection,
        decisionBySection,
      ]
    );

  const plan =
    prepared.ok
      ? prepared.plan
      : null;

  const summary =
    plan?.summary || {
      imported: 0,
      skipped: 0,
      added: 0,
      appended: 0,
      replaced: 0,
      importedWords: 0,
      removedWords: 0,
      affectedUnitIds: [],
    };

  const imported =
    Number(
      summary.imported || 0
    );

  const skipped =
    Number(
      summary.skipped || 0
    );

  const allSkipped =
    Boolean(
      prepared.ok &&
      imported === 0 &&
      skipped > 0
    );

  const canApply =
    Boolean(
      plan &&
      imported > 0 &&
      typeof
        onApplyManuscriptImport ===
        "function"
    );

  const replacementTargetLabels =
    replacementUnitLabels(
      pendingReplacement,
      bookMapUnits,
      isFR
    );

  const replacementTargets =
    naturalList(
      replacementTargetLabels,
      isFR
    ) ||
    (
      isFR
        ? "les pages ou doubles pages sélectionnées"
        : "the selected pages or two-page spreads"
    );

  const ui = {
    title: isFR
      ? "Résumé final de l’import"
      : "Final import summary",

    unresolved: isFR
      ? "Terminez les choix ci-dessus avant d’importer le manuscrit."
      : "Finish the choices above before importing the manuscript.",

    nothing: isFR
      ? "Aucune section n’est sélectionnée pour l’import. Votre livre ne sera pas modifié."
      : "Nothing is selected for import. Your book will not change.",

    importAction: isFR
      ? "Importer dans le livre"
      : "Import into book",

    importing: isFR
      ? "Importation…"
      : "Importing…",

    stale: isFR
      ? "Ce livre a été modifié dans une autre session. Rouvrez-le avant d’importer."
      : "This book changed in another session. Reopen it before importing.",

    failed: isFR
      ? "Le manuscrit n’a pas pu être importé. Vos choix sont toujours disponibles."
      : "The manuscript could not be imported. Your choices are still here.",

    replaceTitle: isFR
      ? "Remplacer le texte actuel du manuscrit ?"
      : "Replace Current Manuscript Text?",

    replaceAction: isFR
      ? "Remplacer le texte et importer"
      : "Replace text and import",

    cancel: isFR
      ? "Annuler"
      : "Cancel",
  };

  const summaryText =
    isFR
      ? `${
          countLabel(
            imported,
            "section sera importée.",
            "sections seront importées."
          )
        } ${
          summary.added
        } ajoutée(s), ${
          summary.appended
        } complétée(s), ${
          summary.replaced
        } remplacée(s). ${
          skipped
        } laissée(s) de côté.`
      : `${
          countLabel(
            imported,
            "section will be imported.",
            "sections will be imported."
          )
        } ${
          summary.added
        } added, ${
          summary.appended
        } appended, ${
          summary.replaced
        } replaced. ${
          skipped
        } left out.`;

  function applicationRequestSnapshot() {
    const safeSections =
      (
        Array.isArray(
          sections
        )
          ? sections
          : []
      ).map(
        (section) => ({
          ...(
            section &&
            typeof section ===
              "object" &&
            !Array.isArray(
              section
            )
              ? section
              : {}
          ),
        })
      );

    const safeMapping =
      mappingBySection &&
      typeof mappingBySection ===
        "object" &&
      !Array.isArray(
        mappingBySection
      )
        ? {
            ...mappingBySection,
          }
        : {};

    const safeDecisions =
      decisionBySection &&
      typeof decisionBySection ===
        "object" &&
      !Array.isArray(
        decisionBySection
      )
        ? {
            ...decisionBySection,
          }
        : {};

    return {
      sections:
        safeSections,

      mappingBySection:
        safeMapping,

      decisionBySection:
        safeDecisions,
    };
  }


  async function applyPlan(
    planToApply,
    applicationRequest
  ) {
    if (
      !planToApply ||
      typeof
        onApplyManuscriptImport !==
        "function"
    ) {
      return;
    }

    try {
      setApplying(true);

      setNotice({
        type: "",
        message: "",
      });

      const result =
        await onApplyManuscriptImport(
          applicationRequest
        );

      if (!result?.ok) {
        setNotice({
          type: "error",

          message:
            Number(
              result?.status || 0
            ) === 409
              ? ui.stale
              : clean(
                  result?.error
                ) ||
                ui.failed,
        });

        return;
      }

      const importedCount =
        Number(
          planToApply
            ?.summary
            ?.imported || 0
        );

      const successMessage =
        result.persisted
          ? (
              isFR
                ? `${
                    importedCount
                  } section${
                    importedCount === 1
                      ? ""
                      : "s"
                  } ${
                    importedCount === 1
                      ? "a été importée et enregistrée"
                      : "ont été importées et enregistrées"
                  } dans ce livre.`
                : `${
                    importedCount
                  } section${
                    importedCount === 1
                      ? ""
                      : "s"
                  } ${
                    importedCount === 1
                      ? "was"
                      : "were"
                  } imported and saved to this book.`
            )
          : (
              isFR
                ? `${
                    importedCount
                  } section${
                    importedCount === 1
                      ? ""
                      : "s"
                  } ${
                    importedCount === 1
                      ? "a été importée"
                      : "ont été importées"
                  } dans ce brouillon. Enregistrez le Blueprint pour conserver les modifications.`
                : `${
                    importedCount
                  } section${
                    importedCount === 1
                      ? ""
                      : "s"
                  } ${
                    importedCount === 1
                      ? "was"
                      : "were"
                  } imported into this temporary draft. These changes will be lost if you refresh or leave this page.`
            );

      if (
        typeof onApplied ===
        "function"
      ) {
        onApplied({
          type: "success",
          message:
            successMessage,
          summary:
            planToApply.summary,

          manuscriptPlan:
            planToApply
              .nextManuscriptPlan,

          result,
        });

        return;
      }

      setNotice({
        type: "success",
        message:
          successMessage,
      });
    } catch (error) {
      setNotice({
        type: "error",

        message:
          clean(
            error?.message
          ) ||
          ui.failed,
      });
    } finally {
      setApplying(false);
    }
  }

  function requestApply() {
    if (!plan) return;

    const applicationRequest =
      applicationRequestSnapshot();

    if (
      plan
        .requiresReplacementConfirmation
    ) {
      setPendingReplacement({
        ...plan,

        applicationRequest,
      });

      return;
    }

    applyPlan(
      plan,
      {
        ...applicationRequest,

        replacementConfirmed:
          false,
      }
    );
  }

  function cancelReplacement() {
    if (applying) return;

    setPendingReplacement(
      null
    );
  }

  async function confirmReplacement(
    event
  ) {
    event.preventDefault();

    const planToApply =
      pendingReplacement;

    const applicationRequest =
      planToApply
        ?.applicationRequest;

    if (
      !planToApply ||
      !applicationRequest
    ) {
      return;
    }

    setPendingReplacement(
      null
    );

    await applyPlan(
      planToApply,
      {
        ...applicationRequest,

        replacementConfirmed:
          true,
      }
    );
  }

  return (
    <section
      aria-label={
        ui.title
      }
      style={{
        padding: 16,
        borderRadius: 14,
        border:
          prepared.ok
            ? "1px solid #86efac"
            : "1px solid #fdba74",
        background:
          prepared.ok
            ? "#f0fdf4"
            : "#fff7ed",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 720,
          }}
        >
          <strong
            style={{
              display: "block",
              fontSize: 17,
            }}
          >
            {ui.title}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "6px 0 0",
              lineHeight: 1.55,
            }}
          >
            {!prepared.ok
              ? ui.unresolved
              : allSkipped
                ? ui.nothing
                : summaryText}
          </p>
        </div>

        {canApply ? (
          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              applying
            }
            aria-busy={
              applying
            }
            onClick={
              requestApply
            }
          >
            {applying
              ? ui.importing
              : ui.importAction}
          </button>
        ) : null}
      </div>

      {notice.message ? (
        <div
          role={
            notice.type ===
            "error"
              ? "alert"
              : "status"
          }
          aria-live="polite"
          style={{
            marginTop: 13,
            padding: "11px 13px",
            borderRadius: 11,
            background:
              notice.type ===
              "error"
                ? "#fef2f2"
                : "#ecfdf5",
            border:
              notice.type ===
              "error"
                ? "1px solid #fecaca"
                : "1px solid #a7f3d0",
          }}
        >
          {notice.message}
        </div>
      ) : null}

      {pendingReplacement ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <form
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="manuscript-import-replace-title"
            aria-describedby="manuscript-import-replace-description"
            onSubmit={
              confirmReplacement
            }
            className="ns-card"
            style={{
              width:
                "min(560px, 100%)",
              padding: 24,
              borderRadius: 18,
              border:
                "1px solid rgba(220,38,38,0.30)",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
          >
            <h3
              id="manuscript-import-replace-title"
              className="ns-card-title"
              style={{
                margin: "0 0 10px",
              }}
            >
              {ui.replaceTitle}
            </h3>

            <p
              id="manuscript-import-replace-description"
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.65,
              }}
            >
              {isFR
                ? `Cette action retirera ${
                    Number(
                      pendingReplacement
                        ?.summary
                        ?.removedWords ||
                        0
                    ).toLocaleString()
                  } mots actuellement présents dans ${replacementTargets}. Le reste du manuscrit restera inchangé.`
                : `This will remove ${
                    Number(
                      pendingReplacement
                        ?.summary
                        ?.removedWords ||
                        0
                    ).toLocaleString()
                  } current words from ${replacementTargets}. The rest of your manuscript will stay unchanged.`}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 22,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  applying
                }
                onClick={
                  cancelReplacement
                }
              >
                {ui.cancel}
              </button>

              <button
                className="ns-button"
                type="submit"
                disabled={
                  applying
                }
                style={{
                  background:
                    "#b91c1c",
                  borderColor:
                    "#b91c1c",
                  color:
                    "#ffffff",
                }}
              >
                {applying
                  ? ui.importing
                  : ui.replaceAction}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}
